import { Combobox } from '@headlessui/react';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import SelectedOptionSvg from '@/shared/assets/icons/ui-kit/option-selected.svg';
import { transformAttribute } from '@/shared/lib/helpers/transform-attribute.helper';
import { useElementWidth } from '@/shared/lib/hooks/use-element-width.hook';
import { useMatchMedia } from '@/shared/lib/hooks/use-match-media.hook';
import { useSearch } from '@/shared/lib/hooks/use-search.hook';
import { Icon } from '@/shared/ui/icon';

import { SearchComponent } from '../autocomplete/autocomplete.component';
import { LongText } from '../long-text';
import { Text } from '../text';
import * as Styled from './multiselect.styles';
import { AutocompleteProps } from './multiselect.types';

const ALL = 'ALL';

const MultiselectComponent = <T extends string>({
	options,
	label,
	values,
	setData,
	searchPlaceholder,
	variant = 'primary',
	setAll,
	isAll,
	required,
	placeholder,
	width,
	disabled,
	disabledInfo,
}: AutocompleteProps<T>) => {
	const { t } = useTranslation('common');
	const { select } = useTheme();
	const [deferredQuery, filteredOptions, onFilter, setQuery] = useSearch(options, true);
	const isMobile = useMatchMedia('mobile');
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const { width: buttonWidth } = useElementWidth(buttonRef);

	const keys = useMemo(() => options.map(({ value }) => value), [options]);
	const input = values?.map((v) => options.find(({ value }) => v === value)?.label).join(', ');

	const onRemovePropagation = (e: MouseEvent) => {
		e.stopPropagation();
	};

	const onClearSearch = useCallback(() => {
		setQuery('');
	}, [setQuery]);

	const onVariablesChange = (newValues: string[]) => {
		const isAllSelected = newValues.includes(ALL);

		if (!isAllSelected) {
			setData(newValues);
		}

		if (keys.length !== newValues.length && !isAllSelected) {
			setAll(false);
		} else if (keys.length === newValues.length && !isAllSelected) {
			setAll(true);
		}
	};

	const setAllStateChange = (isAll: boolean) => {
		if (isAll) {
			setData(keys);
		} else {
			setData([]);
		}

		setAll(isAll);
	};

	const handleOpen = (open: boolean) => {
		if (open) return;

		setQuery('');
	};

	return (
		<Combobox multiple value={values} onChange={onVariablesChange}>
			{({ open }) => (
				<Styled.Multiselect position="relative" direction="column" label={label} width={width} disabled={disabled}>
					{label && (
						<Styled.Label
							label={label}
							Component={
								required && (
									<>
										{label}
										<span>*</span>
									</>
								)
							}
						/>
					)}
					<Styled.Button
						ref={buttonRef}
						arrow={transformAttribute(Boolean(true))}
						variant={variant}
						open={open}
						hasValue={Boolean(input)}
						onClick={() => handleOpen(open)}
					>
						<LongText
							label={input || placeholder}
							length={isMobile ? 25 : 30}
							flexDirection="row"
							maxWidth={`${buttonWidth}px`}
							width={`${buttonWidth}px`}
						/>

						<Styled.MultiselectArrow position="absolute" open={open} disabled={disabled}>
							<Icon Svg={select.arrow} />
						</Styled.MultiselectArrow>
					</Styled.Button>
					{disabled && disabledInfo && <Text label={disabledInfo} size="xs" color="secondary" />}
					<Styled.Options
						border={transformAttribute(Boolean(filteredOptions.length))}
						scroll={transformAttribute(filteredOptions.length > 5)}
						variant={variant}
					>
						<SearchComponent
							variant={variant}
							paragraph="0"
							deferredQuery={deferredQuery}
							removePropagation={onRemovePropagation}
							onFilter={onFilter}
							onClearSearch={onClearSearch}
							placeholder={searchPlaceholder}
						/>
						{filteredOptions.length === 0 && deferredQuery && (
							<Styled.NoMatchesOption>
								<Text label={t('text.noMatches')} />
							</Styled.NoMatchesOption>
						)}
						{Boolean(filteredOptions.length) && (
							<Styled.Option
								value={t('autocomplete.all')}
								onClick={() => {
									setAllStateChange(!isAll);
								}}
							>
								<Styled.Selected selected={isAll}>
									{isAll && (
										<Styled.CheckIconWrapper>
											<Icon Svg={SelectedOptionSvg} fill="currentColor" />
										</Styled.CheckIconWrapper>
									)}
									<span>{t('autocomplete.all')}</span>
								</Styled.Selected>
							</Styled.Option>
						)}
						{filteredOptions.map(({ value, label }) => (
							<Styled.Option key={value} value={value}>
								{({ selected }) => {
									return (
										<Styled.Selected selected={selected}>
											{selected && (
												<Styled.CheckIconWrapper>
													<Icon Svg={SelectedOptionSvg} fill="currentColor" />
												</Styled.CheckIconWrapper>
											)}
											<span>{label}</span>
										</Styled.Selected>
									);
								}}
							</Styled.Option>
						))}
					</Styled.Options>
				</Styled.Multiselect>
			)}
		</Combobox>
	);
};

export default memo(MultiselectComponent);
